<template>
  <div
    v-if="hasWarning"
    class="deletion-warning-banner fixed top-0 left-0 right-0 z-[99] bg-orange-500 text-white px-4 py-2 flex items-center justify-center gap-3 shadow-md"
    :class="{ 'top-10': authStore.isImpersonating }"
  >
    <i class="pi pi-exclamation-circle text-lg"></i>
    <span class="font-medium text-sm">
      {{ t('deletion_warning.banner.message', { date: deletionDate, reason: reasonLabel }) }}
    </span>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import apiClient from '@/services/api'

const { t } = useI18n({ useScope: 'global' })
const authStore = useAuthStore()

const hasWarning = ref(false)
const deletionDate = ref('')
const reason = ref('')

const reasonLabel = computed(() => {
  return t(`deletion_warning.reasons.${reason.value}`)
})

async function checkDeletionWarning() {
  // This component lives outside router-view, so it mounts before the guard
  // settles. Wait for the session to be decided, then ask only when the
  // access token is actually usable. Gating on "has a session" would fire
  // this on the public payment page with a stale access token, producing the
  // 401 the requirement forbids (SEZ-5044-R08).
  await authStore.sessionReady
  if (!authStore.hasValidAccessToken()) return
  try {
    const res = await apiClient.get('/enercare/api/v1/auth/deletion-warning/')
    if (res.data.has_warning) {
      hasWarning.value = true
      deletionDate.value = res.data.deletion_date
      reason.value = res.data.reason
    } else {
      hasWarning.value = false
    }
  } catch {
    // Silently fail — don't block the UI
  }
}

onMounted(checkDeletionWarning)

// Re-check when auth state changes (e.g., after login)
watch(() => authStore.isAuthenticated, (val) => {
  if (val) checkDeletionWarning()
  else hasWarning.value = false
})
</script>

<style scoped>
.deletion-warning-banner {
  font-family: inherit;
}
</style>
